import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Download, ExternalLink, Search } from "lucide-react";
import { toast } from "sonner";

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/leads", { params: { search: search || undefined } });
      setLeads(data);
    } catch (e) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const exportCsv = async () => {
    try {
      const tok = localStorage.getItem("elvis_admin_token");
      const res = await fetch(`${API}/admin/leads/export`, { headers: { Authorization: `Bearer ${tok}` } });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "elvis_leads.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  return (
    <div className="p-8 lg:p-12 max-w-7xl">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="overline">Inbox</p>
          <h1 className="editorial-h text-5xl text-brand-dark mt-2">Leads</h1>
        </div>
        <Button onClick={exportCsv} data-testid="leads-export-btn" className="rounded-none bg-brand-primary hover:bg-brand-primaryHover text-brand-bone gap-2">
          <Download strokeWidth={1.5} className="w-4 h-4" /> Export CSV
        </Button>
      </div>

      <div className="mt-6 flex items-center gap-2 max-w-md">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-muted" strokeWidth={1.5} />
          <Input
            data-testid="leads-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, phone, ref…"
            className="rounded-none pl-9 h-11 border-brand-line bg-white focus-visible:border-brand-primary focus-visible:ring-brand-primary"
          />
        </div>
        <Button onClick={load} variant="outline" className="rounded-none border-brand-line h-11">Search</Button>
      </div>

      <div className="bg-white border border-brand-line mt-8 overflow-x-auto">
        <table className="w-full text-sm" data-testid="leads-table">
          <thead className="bg-brand-elevated">
            <tr className="text-left">
              <th className="px-4 py-3 font-sans text-xs uppercase tracking-widest text-brand-muted">Ref</th>
              <th className="px-4 py-3 font-sans text-xs uppercase tracking-widest text-brand-muted">When</th>
              <th className="px-4 py-3 font-sans text-xs uppercase tracking-widest text-brand-muted">Customer</th>
              <th className="px-4 py-3 font-sans text-xs uppercase tracking-widest text-brand-muted">Total</th>
              <th className="px-4 py-3 font-sans text-xs uppercase tracking-widest text-brand-muted">Rep</th>
              <th className="px-4 py-3 font-sans text-xs uppercase tracking-widest text-brand-muted">Method</th>
              <th className="px-4 py-3 font-sans text-xs uppercase tracking-widest text-brand-muted">WA</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="8" className="px-4 py-6 text-center text-brand-muted">Loading…</td></tr>
            )}
            {!loading && leads.length === 0 && (
              <tr><td colSpan="8" className="px-4 py-6 text-center text-brand-muted">No leads yet.</td></tr>
            )}
            {leads.map((l) => (
              <tr key={l.id} className="border-t border-brand-line hover:bg-brand-elevated cursor-pointer" onClick={() => setActive(l)} data-testid={`lead-row-${l.lead_ref}`}>
                <td className="px-4 py-3 font-mono text-xs">#{l.lead_ref}</td>
                <td className="px-4 py-3 text-xs text-brand-muted">{new Date(l.created_at).toLocaleString()}</td>
                <td className="px-4 py-3">
                  <p className="font-semibold text-brand-dark">{l.name}</p>
                  <p className="text-xs text-brand-muted">{l.phone}</p>
                </td>
                <td className="px-4 py-3 font-semibold">₹{l.total}</td>
                <td className="px-4 py-3 text-xs">{l.rep_name}<br /><span className="text-brand-muted">{l.assignment_method}</span></td>
                <td className="px-4 py-3 text-xs">{l.payment_method === "online" ? "Online" : "COD"}</td>
                <td className="px-4 py-3 text-xs">{l.wa_opened ? "✓" : "—"}</td>
                <td className="px-4 py-3 text-xs">
                  <button onClick={(e) => { e.stopPropagation(); setActive(l); }} className="text-brand-primary hover:underline">View</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {active && (
        <div className="fixed inset-0 z-50 flex" onClick={() => setActive(null)} data-testid="lead-detail">
          <div className="flex-1 bg-brand-dark/40" />
          <div className="w-full max-w-xl bg-brand-bone overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-brand-line flex items-center justify-between">
              <div>
                <p className="overline">Lead</p>
                <p className="font-serif text-3xl text-brand-dark">#{active.lead_ref}</p>
              </div>
              <button onClick={() => setActive(null)} className="text-brand-dark text-2xl" data-testid="lead-detail-close">×</button>
            </div>
            <div className="p-6 space-y-4">
              <a href={`https://wa.me/${active.rep_phone}?text=${encodeURIComponent(active.wa_message || "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-brand-primary text-brand-bone px-4 py-2 text-sm font-semibold" data-testid="lead-detail-wa">
                Open in WhatsApp <ExternalLink strokeWidth={1.5} className="w-3 h-3" />
              </a>
              <div className="bg-white border border-brand-line p-5">
                <p className="overline mb-2">Customer</p>
                <p className="text-sm"><span className="text-brand-muted">Name:</span> {active.name}</p>
                <p className="text-sm"><span className="text-brand-muted">Phone:</span> {active.phone}</p>
                {active.alt_phone && <p className="text-sm"><span className="text-brand-muted">Alt phone:</span> {active.alt_phone}</p>}
                <p className="text-sm"><span className="text-brand-muted">Age/Gender:</span> {active.age || "—"} / {active.gender || "—"}</p>
              </div>
              <div className="bg-white border border-brand-line p-5">
                <p className="overline mb-2">Delivery</p>
                <p className="text-sm">{active.address}</p>
                <p className="text-sm">{active.city}, {active.district}, {active.state} — {active.pincode}</p>
              </div>
              <div className="bg-white border border-brand-line p-5">
                <p className="overline mb-2">Items · ₹{active.total}</p>
                {(active.items || []).map((it, i) => (
                  <div key={i} className="flex justify-between text-sm py-1 border-b border-brand-line last:border-0">
                    <span>{it.name} × {it.qty}</span>
                    <span>₹{it.price * it.qty}</span>
                  </div>
                ))}
              </div>
              <div className="bg-white border border-brand-line p-5">
                <p className="overline mb-2">WhatsApp message</p>
                <pre className="text-xs text-brand-muted whitespace-pre-wrap font-sans">{active.wa_message}</pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
